import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';

const badgeVariants = cva(
  'inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md px-2 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&>svg]:size-3 [&>svg]:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground focus-visible:ring-destructive/20',
        /**
         * Carries its own `border` width, because the base has none — every
         * other variant sets `border-transparent`, a colour with nothing to
         * colour, so adding the width to the base would widen all of them by
         * two pixels.
         *
         * The line is `--muted-foreground` rather than `--border`. `--border` is
         * `oklch(0.922 0 0)`, which against this app's light fills is five
         * percent of lightness at one pixel — a border you have to be told
         * about. This one is for saying *this is the one*, so it has to read.
         */
        outline: 'border border-muted-foreground bg-background text-foreground',
      },
      tone: {
        primary:
          'border-transparent bg-primary-subtle text-primary-subtle-foreground',
        success:
          'border-transparent bg-success-subtle text-success-subtle-foreground',
        warning:
          'border-transparent bg-warning-subtle text-warning-subtle-foreground',
        info: 'border-transparent bg-info-subtle text-info-subtle-foreground',
        destructive:
          'border-transparent bg-destructive-subtle text-destructive-subtle-foreground',
        neutral: 'border-transparent bg-muted text-muted-foreground',
      },
      shape: {
        default: '',
        pill: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      shape: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  tone,
  shape = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-tone={tone}
      className={cn(badgeVariants({ variant, tone, shape }), className)}
      {...props}
    />
  );
}

type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export { Badge, badgeVariants };
export type { BadgeTone };
